import { useWindowEvent } from '@mantine/hooks';
import { useState } from 'react';
import type { WindowSize } from './useWindowSize.type';

const eventListenerOptions = { passive: true };

/**
 * Reads the current viewport dimensions from `window` (or returns `{ 0, 0 }`
 * when `window` is undefined). Exported solely so the SSR branch can be
 * unit-tested without a React renderer; consumers should call `useWindowSize`
 * instead.
 *
 * @returns {WindowSize} Current viewport dimensions, or `{ 0, 0 }` if `window` is undefined.
 */
const readWindowSize = (): WindowSize =>
  typeof window === 'undefined' ? { width: 0, height: 0 } : { width: window.innerWidth, height: window.innerHeight };

/**
 * Returns the current viewport width and height. Updates on `resize` and
 * `orientationchange` window events.
 *
 * The initial render returns real `window.innerWidth`/`innerHeight` (read
 * synchronously in the state initializer), avoiding a first-render placeholder
 * value on consumers that read the size during initial state setup — e.g.,
 * `useDisclosure(height > breakpoint)` where the disclosure only reads its
 * initial value once.
 *
 * Resize handlers compare-and-bail: identical dimensions return the previous
 * state reference, so consecutive `resize` events with the same final size
 * (common during `orientationchange` and edge-drag idle frames) do not
 * trigger downstream re-renders.
 *
 * @returns {WindowSize} Current viewport dimensions. `{ 0, 0 }` only when
 *   `typeof window === 'undefined'` (jsdom-without-window or SSR). This app
 *   is SPA-only; the 0/0 branch is dead code in production.
 */
const useWindowSize = (): WindowSize => {
  const [size, setSize] = useState<WindowSize>(readWindowSize);

  const handleResize = () => {
    setSize((prev) => {
      const next = readWindowSize();
      return prev.width === next.width && prev.height === next.height ? prev : next;
    });
  };

  useWindowEvent('resize', handleResize, eventListenerOptions);
  useWindowEvent('orientationchange', handleResize, eventListenerOptions);

  return size;
};

export { readWindowSize, useWindowSize };
